/**
 * FileDropzone — drag-and-drop single-file picker.
 *
 * Ported from wal-frontend-v2's FileUpload. The portal's own FileUpload is an image cropper and
 * cannot take a spreadsheet, hence the separate component.
 */
import { Plus } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';

import ButtonCmp from '../ButtonCmp';

interface IFileUploadProps {
    label?: string;
    labelClassName?: string;
    placeholder?: string;
    buttonLabel?: string;
    accept?: Record<string, string[]>;
    maxFiles?: number;
    onFileSelect: (file: File | null) => void;
    onFileRejected?: (errors: any[]) => void;
    errorText?: string;
    isError?: boolean;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    parentClassName?: string;
}

const FileDropzone: React.FC<IFileUploadProps> = ({
    label,
    labelClassName = 'form_label block mb-1.5',
    placeholder = 'Click to upload or drag and drop an .xlsx file',
    buttonLabel = 'Add Files',
    accept = { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] },
    maxFiles = 1,
    onFileSelect,
    onFileRejected,
    errorText,
    isError,
    disabled = false,
    required = false,
    className = '',
    parentClassName = '',
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            const file = acceptedFiles[0];

            if (file) {
                onFileSelect(file);
            }
        },
        [onFileSelect]
    );

    const onDropRejected = useCallback(
        (rejectedFiles: any[]) => {
            onFileSelect(null);
            if (onFileRejected) onFileRejected(rejectedFiles);
        },
        [onFileSelect, onFileRejected]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        maxFiles,
        multiple: false,
        accept,
        disabled,
    });

    return (
        <div className={parentClassName}>
            {label && (
                <label className={labelClassName}>
                    {label}
                    {required && <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>}
                </label>
            )}
            <div
                {...getRootProps()}
                className={`cursor-pointer rounded-xl border border-dashed ${
                    errorText || isError ? 'border-red-500' : isDragActive ? 'border-primary bg-blue-50' : 'border-gray500'
                } py-4 min-h-[123px] flex items-center justify-center px-4 text-center bg-white transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            >
                <div className="flex flex-col items-center justify-center space-y-1.5">
                    <ButtonCmp type="button" className="btn_secondary_black" icon={<Plus className="w-4 h-4" />} iconSide="left">
                        {buttonLabel}
                    </ButtonCmp>
                    <div className="text-center">
                        <p className="text-[12px] leading-[18px] font-normal text-gray500">{placeholder}</p>
                    </div>
                </div>
                <input type="file" ref={fileInputRef} className="hidden" {...getInputProps()} />
            </div>
            {errorText && <p className="text-red-500 text-xs mt-1.5">{errorText}</p>}
        </div>
    );
};

export default FileDropzone;
